import { CreateMailTemplateRequestDto, MailTemplateResponseDto } from "@/lib/interfaces/mail";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Textarea,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { EditorState, Modifier, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import { Plus, Save, Trash } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import { convertHtmlToEditorState } from "../../lib/utils/convertHtml";
import CreateEditor from "../../lib/utils/richtext-editor";
import { Form, FormField, FormItem, FormMessage } from "../../ui/components/ui/form";
import { Input } from "../../ui/components/ui/input";
import Placeholder from "./Placeholder";
import ConfirmationDialog from "../../ui/components/confirmation-dialog";

const OwnEmailsTabPanel = ({ placeholders, mailLabel }: { placeholders: string[]; mailLabel: string }) => {
  const { user } = useUser();
  const [mailTemplates, setMailTemplates] = useState<MailTemplateResponseDto[] | undefined>();
  const [reload, setReload] = useState(false);

  const { apiRequest } = useApiRequest();

  const getEmailTemplate = useCallback(async () => {
    const response = await apiRequest<MailTemplateResponseDto[]>(`mail-templates/label/${mailLabel}`, "GET");

    if (response?.data) {
      setMailTemplates(response.data);
    } else {
      setMailTemplates(undefined);
    }
  }, [apiRequest, mailLabel]);

  useEffect(() => {
    getEmailTemplate();
  }, [apiRequest, getEmailTemplate, reload]);

  const canAdd =
    user?.permissionMailTemplates === "WRITE" ||
    user?.permissionMailTemplates === "FULL" ||
    user?.permissionOrganizationAdmin;

  return (
    <Flex className="p-8 rounded border border-gray-300 gap-8 flex-col h-full bg-white">
      {mailTemplates ? (
        <>
          <Tabs position="relative" height="100%" flex="1" display="flex" flexDirection="column" isLazy>
            <TabList
              overflowX="auto"
              overflowY="hidden"
              whiteSpace="nowrap"
              paddingBottom="5px"
              borderBottom="none"
              sx={{
                "::-webkit-scrollbar": {
                  height: "4px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                },
                "::-webkit-scrollbar-thumb:hover": {
                  background: "#555",
                },
                "[aria-selected='false']": {
                  borderBottom: "2px solid rgb(209 213 219 / 50%)",
                },
              }}
            >
              {mailTemplates.map((item, index) => {
                return (
                  <Tab sx={{ color: "#7C8080" }} key={index}>
                    {item.title}
                  </Tab>
                );
              })}
              {canAdd && (
                <Tab sx={{ color: "#7C8080" }}>
                  <Plus />
                </Tab>
              )}
            </TabList>
            <TabPanels display="flex" flexDirection="column" flex="1">
              {mailTemplates.map((item, index) => {
                return (
                  <TabPanel key={index}>
                    <EmailField mailTemplate={item} placeholders={placeholders} setReload={setReload} />
                  </TabPanel>
                );
              })}
              {canAdd && (
                <TabPanel>
                  <EmailField placeholders={placeholders} setReload={setReload} />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </>
      ) : (
        <Flex className="justify-center w-full h-52 items-center">
          <Spinner size="xl" color="primary.500" />
        </Flex>
      )}
    </Flex>
  );
};

const EmailField = ({
  placeholders,
  mailTemplate,
  setReload,
}: {
  placeholders: string[];
  mailTemplate?: MailTemplateResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();
  const { user } = useUser();
  const [templateEditorState, setTemplateEditorState] = useState(() =>
    convertHtmlToEditorState(mailTemplate?.template || "")
  );

  const formSchema = z.object({
    title: z.string().min(1),
    description: z.string().min(1),
    subject: z.string().min(1),
    template: z.string().refine(
      () => {
        const htmlContentDescription = draftToHtml(convertToRaw(templateEditorState.getCurrentContent()));
        return htmlContentDescription.length > 7;
      },
      {
        message: "Bitte geben Sie eine Beschreibung ein",
      }
    ),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: mailTemplate?.title || "",
      description: mailTemplate?.description || "",
      subject: mailTemplate?.subject || "",
      template: mailTemplate?.template || "",
    },
  });

  const createTemplate = async (values: z.infer<typeof formSchema>) => {
    const htmlContent = draftToHtml(convertToRaw(templateEditorState.getCurrentContent()));

    const req: CreateMailTemplateRequestDto = {
      title: values.title,
      description: values.description,
      template: htmlContent,
      subject: values.subject,
    };

    if (mailTemplate) {
      await apiRequest(`mail-templates/${mailTemplate._id}`, "PATCH", {
        body: req,
        toast: { toastText: "Email-Vorlage wurde erfolgreich aktualisiert" },
      });
    } else {
      await apiRequest(`mail-templates`, "POST", {
        body: req,
        toast: { toastText: "Email-Vorlage wurde erfolgreich erstellt" },
      });
    }
    setReload((prev) => !prev);
  };

  const deleteTemplate = async () => {
    await apiRequest(`mail-templates/${mailTemplate?._id}`, "DELETE", {
      toast: { toastText: "Email-Vorlage wurde erfolgreich gelöscht" },
    });
    setReload((prev) => !prev);
  };

  if (!user) return null;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(createTemplate, (error) => console.log(error))}>
        <Flex className="gap-8 mb-6">
          <Box className="w-2/5">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex flex-col mb-4 w-full">
                  <FormLabel className="w-full font-bold mt-4">
                    Name<span className="text-primary">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="flex flex-col mb-4 w-full">
                  <FormLabel className="w-full font-bold mt-4">
                    Beschreibung<span className="text-primary">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder="Beschreibung" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Box>
          <Box className="w-3/5">
            <div className="flex flex-col gap-3 w-full mb-5">
              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem className="flex flex-col mb-4 w-full">
                    <FormLabel className="w-full font-bold mt-4">
                      Betreff<span className="text-primary">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Betreff" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Placeholder
              placeholders={placeholders}
              onPlaceholderClicked={(placeholder) => {
                const contentState = templateEditorState.getCurrentContent();
                const selectionState = templateEditorState.getSelection();

                const newContentState = Modifier.insertText(contentState, selectionState, `[${placeholder}]`);

                const newEditorState = EditorState.push(templateEditorState, newContentState, "insert-characters");
                setTemplateEditorState(newEditorState);
              }}
            />
            <CreateEditor editorState={templateEditorState} setEditorState={setTemplateEditorState} />
          </Box>
        </Flex>
        <Flex className="gap-4 justify-end mt-auto">
          <Button
            colorScheme="primary"
            color="white"
            className="flex gap-2 p-2 text-white"
            variant="solid"
            type="submit"
          >
            <Box boxSize={5} display="flex" alignItems="center" justifyContent="center">
              <Save />
            </Box>
            <span>Speichern</span>
          </Button>
          {mailTemplate && (
            <ConfirmationDialog
              onConfirm={deleteTemplate}
              title="Löschen"
              description="Sind Sie sicher, dass Sie diese Email-Vorlage löschen möchten?"
              variant="destructive"
            >
              <Button colorScheme="red">
                <Box boxSize={5} display="flex" alignItems="center" justifyContent="center">
                  <Trash />
                </Box>
                <span>Löschen</span>
              </Button>
            </ConfirmationDialog>
          )}
        </Flex>
      </form>
    </Form>
  );
};

export default OwnEmailsTabPanel;
